import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, del } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { GripVertical, Plus, Pencil, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const EMPTY_PIPELINE = { name: "", description: "", project_type_id: null, color: "#3B82F6" };
const EMPTY_STAGE = { name: "", type: "standard", sla_days: "", color: "#94A3B8" };

export default function Pipelines() {
  const qc = useQueryClient();
  const { can } = useApp();
  const [pipeModal, setPipeModal] = useState(null); // { mode, data }
  const [stageModal, setStageModal] = useState(null); // { mode, pipeline, data }
  const [dragStage, setDragStage] = useState(null); // { pipelineId, stageId }

  const { data: pipelines, isLoading } = useQuery({ queryKey: ["pipelines"], queryFn: () => get("/crm/pipelines") });
  const { data: types = [] } = useQuery({ queryKey: ["project-types"], queryFn: () => get("/crm/project-types") });
  const typeMap = Object.fromEntries(types.map((t) => [t.id, t]));

  const createPipe = useMutation({
    mutationFn: (body) => post("/crm/pipelines", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pipelines"] }); toast.success("Pipeline created"); setPipeModal(null); },
  });
  const updatePipe = useMutation({
    mutationFn: ({ id, body }) => patch(`/crm/pipelines/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pipelines"] }); toast.success("Pipeline updated"); setPipeModal(null); },
  });
  const deletePipe = useMutation({
    mutationFn: (id) => del(`/crm/pipelines/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pipelines"] }); toast.success("Pipeline deleted"); },
    onError: () => toast.error("Can't delete — leads still use this pipeline"),
  });
  const deleteStage = useMutation({
    mutationFn: ({ pid, sid }) => del(`/crm/pipelines/${pid}/stages/${sid}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pipelines"] }); toast.success("Stage deleted"); },
    onError: () => toast.error("Can't delete — leads are still in this stage"),
  });
  const saveStages = useMutation({
    mutationFn: ({ pid, stages }) => patch(`/crm/pipelines/${pid}`, { stages }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pipelines"] }),
  });

  const upsertStage = (pipeline, stageBody, editingId) => {
    const stages = editingId
      ? pipeline.stages.map((s) => (s.id === editingId ? { ...s, ...stageBody } : s))
      : [...pipeline.stages, { ...stageBody, id: `st-${crypto.randomUUID().slice(0, 8)}`, order: pipeline.stages.length }];
    saveStages.mutate({ pid: pipeline.id, stages });
    setStageModal(null);
  };

  const reorder = (pipeline, fromId, toId) => {
    if (fromId === toId) return;
    const stages = [...pipeline.stages];
    const fromIdx = stages.findIndex((s) => s.id === fromId);
    const toIdx = stages.findIndex((s) => s.id === toId);
    const [moved] = stages.splice(fromIdx, 1);
    stages.splice(toIdx, 0, moved);
    const reindexed = stages.map((s, i) => ({ ...s, order: i }));
    saveStages.mutate({ pid: pipeline.id, stages: reindexed });
  };

  if (isLoading) return <Skeleton className="h-64" />;

  return (
    <div>
      <PageHeader title="Pipelines" subtitle="Configure your sales stages and SLAs.">
        {can("crm", "edit") && (
          <Button data-testid="new-pipeline-btn" onClick={() => setPipeModal({ mode: "new", data: EMPTY_PIPELINE })}>
            <Plus className="mr-1.5 h-4 w-4" />New Pipeline
          </Button>
        )}
      </PageHeader>

      {pipelines?.map((p) => (
        <Section key={p.id} title={
          <span className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full" style={{ backgroundColor: p.color }} />
            {p.name}
            {p.project_type_id && typeMap[p.project_type_id] && (
              <StatusBadge family="slate" label={typeMap[p.project_type_id].name} />
            )}
          </span>
        } className="mb-4" testid={`pipeline-${p.id}`}
          action={
            <div className="flex items-center gap-2">
              {p.is_default && <StatusBadge family="blue" label="Default" />}
              {can("crm", "edit") && (
                <>
                  <Button variant="ghost" size="icon" data-testid={`edit-pipeline-${p.id}`}
                    onClick={() => setPipeModal({ mode: "edit", data: p })}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" data-testid={`add-stage-${p.id}`}
                    onClick={() => setStageModal({ mode: "new", pipeline: p, data: EMPTY_STAGE })}>
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
              {can("crm", "delete") && (
                <ConfirmDelete testid={`del-pipeline-${p.id}`} title={`Delete "${p.name}"?`}
                  description="Blocked if any lead still uses this pipeline."
                  trigger={<Button variant="ghost" size="icon"><Trash2 className="h-3.5 w-3.5" /></Button>}
                  onConfirm={() => deletePipe.mutate(p.id)} />
              )}
            </div>
          }>
          <div className="divide-y divide-border">
            {[...p.stages].sort((a, b) => a.order - b.order).map((s) => (
              <div key={s.id} data-testid={`stage-row-${s.id}`}
                draggable={can("crm", "edit")}
                onDragStart={() => setDragStage({ pipelineId: p.id, stageId: s.id })}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragStage?.pipelineId === p.id) reorder(p, dragStage.stageId, s.id); setDragStage(null); }}
                className={cn("flex items-center gap-3 px-4 py-3", dragStage?.stageId === s.id && "opacity-50")}>
                <GripVertical className={cn("h-4 w-4 text-muted-foreground", can("crm", "edit") && "cursor-grab")} />
                <span className="h-3 w-3 shrink-0 rounded-full border border-border" style={{ backgroundColor: s.color || "#94A3B8" }} />
                <span className="flex-1 text-sm font-medium">{s.name}</span>
                {s.type !== "standard" && <StatusBadge status={s.type} />}
                <span className="text-xs text-muted-foreground">{s.sla_days ? `SLA ${s.sla_days}d` : "No SLA"}</span>
                {can("crm", "edit") && (
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" data-testid={`edit-stage-${s.id}`}
                      onClick={() => setStageModal({ mode: "edit", pipeline: p, data: s })}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <ConfirmDelete testid={`del-stage-${s.id}`} title={`Delete "${s.name}" stage?`}
                      description="Blocked if any lead is still in this stage."
                      trigger={<Button variant="ghost" size="icon"><Trash2 className="h-3.5 w-3.5" /></Button>}
                      onConfirm={() => deleteStage.mutate({ pid: p.id, sid: s.id })} />
                  </div>
                )}
              </div>
            ))}
            {p.stages.length === 0 && <div className="px-4 py-6 text-center text-xs text-muted-foreground">No stages yet.</div>}
          </div>
        </Section>
      ))}

      <PipelineModal state={pipeModal} setState={setPipeModal} types={types}
        onSubmit={(body) => pipeModal?.mode === "new" ? createPipe.mutate(body) : updatePipe.mutate({ id: pipeModal.data.id, body })} />
      <StageModal state={stageModal} setState={setStageModal}
        onSubmit={(body) => upsertStage(stageModal.pipeline, body, stageModal.mode === "edit" ? stageModal.data.id : null)} />
    </div>
  );
}

function PipelineModal({ state, setState, types, onSubmit }) {
  const [form, setForm] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const d = state?.data || EMPTY_PIPELINE;
  const val = (k) => (form[k] !== undefined ? form[k] : d[k]);

  return (
    <Dialog open={!!state} onOpenChange={(v) => { if (!v) setState(null); else setForm({}); }} key={d.id || "new-pipe"}>
      <DialogContent className="max-w-md" data-testid="pipeline-modal">
        <DialogHeader><DialogTitle>{state?.mode === "edit" ? "Edit Pipeline" : "New Pipeline"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input data-testid="pipe-name" className="mt-1" value={val("name") || ""} onChange={(e) => set("name", e.target.value)} /></div>
          <div><Label>Description</Label><Textarea className="mt-1" value={val("description") || ""} onChange={(e) => set("description", e.target.value)} /></div>
          <div>
            <Label>Project Type</Label>
            <Select value={val("project_type_id") || "none"} onValueChange={(v) => set("project_type_id", v === "none" ? null : v)}>
              <SelectTrigger className="mt-1" data-testid="pipe-project-type"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">All Types</SelectItem>
                {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Pipeline Color</Label>
            <div className="mt-1 flex items-center gap-2">
              <input type="color" data-testid="pipe-color" className="h-9 w-12 cursor-pointer rounded border border-border bg-transparent"
                value={val("color") || "#3B82F6"} onChange={(e) => set("color", e.target.value)} />
              <Input value={val("color") || "#3B82F6"} onChange={(e) => set("color", e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setState(null)}>Cancel</Button>
          <Button data-testid="pipe-submit" onClick={() => {
            if (!val("name")) { toast.error("Name is required"); return; }
            onSubmit({ name: val("name"), description: val("description") || "", project_type_id: val("project_type_id") ?? null, color: val("color") || "#3B82F6" });
          }}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StageModal({ state, setState, onSubmit }) {
  const [form, setForm] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const d = state?.data || EMPTY_STAGE;
  const val = (k) => (form[k] !== undefined ? form[k] : d[k]);

  return (
    <Dialog open={!!state} onOpenChange={(v) => { if (!v) setState(null); else setForm({}); }} key={d.id || "new-stage"}>
      <DialogContent className="max-w-sm" data-testid="stage-modal">
        <DialogHeader><DialogTitle>{state?.mode === "edit" ? "Edit Stage" : "Add Stage"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Stage Name *</Label><Input data-testid="stage-name" className="mt-1" value={val("name") || ""} onChange={(e) => set("name", e.target.value)} /></div>
          <div>
            <Label>Type</Label>
            <Select value={val("type") || "standard"} onValueChange={(v) => set("type", v)}>
              <SelectTrigger className="mt-1" data-testid="stage-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard</SelectItem>
                <SelectItem value="won">Won</SelectItem>
                <SelectItem value="lost">Lost</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>SLA Days</Label><Input type="number" data-testid="stage-sla" className="mt-1" value={val("sla_days") ?? ""} onChange={(e) => set("sla_days", e.target.value ? Number(e.target.value) : null)} /></div>
          <div>
            <Label>Color</Label>
            <div className="mt-1 flex items-center gap-2">
              <input type="color" data-testid="stage-color" className="h-9 w-12 cursor-pointer rounded border border-border bg-transparent"
                value={val("color") || "#94A3B8"} onChange={(e) => set("color", e.target.value)} />
              <Input value={val("color") || "#94A3B8"} onChange={(e) => set("color", e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setState(null)}>Cancel</Button>
          <Button data-testid="stage-submit" onClick={() => {
            if (!val("name")) { toast.error("Stage name is required"); return; }
            onSubmit({ name: val("name"), type: val("type") || "standard", sla_days: val("sla_days") ?? null, color: val("color") || "#94A3B8" });
          }}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
